import { AfterViewInit, Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSelect } from '@angular/material/select';
import { select, Store } from '@ngrx/store';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { switchMap } from 'rxjs/operators';

import {
  Category,
  CategoryService,
  ContactRelationshipsService,
  ContactsService,
  CreateNoteOnServer,
  Note,
  NoteEntity,
  NoteEntityType,
  NoteMode,
  selectLastCreatedContact,
  UpdateNoteOnServer,
} from '../../../../../../core/lists';
import { AppState } from '../../../../../../core/reducers';
import { currentUser } from '../../../../../../core/auth';
import { StringUtilsService } from '../../../../../../core/services/string-utils.service';
import { FilteredViewService } from '../../../../../../core/services/filtered-view.service';
import { GeneralAutocompleteComponent } from '../../../../../partials/layout/general-autocomplete/general-autocomplete.component';
import { CreateContactDialogComponent } from '../../../../../partials/content/general/contact-add-dialog/contact-add.dialog.component';
import { ConfirmActionDialogComponent } from '../../../../../partials/content/crud';

@Component({
  selector: 'kt-add-note-dialog',
  templateUrl: './add-note-dialog.component.html',
  styleUrls: ['./add-note-dialog.component.scss'],
})
export class AddNoteDialogComponent implements AfterViewInit, OnInit, OnDestroy {
  private subscriptions = new Subscription();
  isCreate: boolean;
  isFilteredView: boolean;
  form: FormGroup;
  submitted = false;
  showCategoryInput = true;
  userCategories: Category[] = [];
  entityTypes: NoteEntityType[] = Object.values(NoteEntityType).filter(
    (t) => t !== NoteEntityType.CONTACT,
  );
  entitiesStringSub = new BehaviorSubject<string>('');
  getEntitiesAutocomplete$: Observable<NoteEntity[]>;
  getContactsAutocomplete$: Observable<NoteEntity[]>;
  @ViewChild('entityTypeSelect', { static: true }) entityTypeSelect: MatSelect;
  @ViewChild('entitiesAutocomplete', { static: true })
  entitiesAutocomplete: GeneralAutocompleteComponent;
  @ViewChild('contactsAutocomplete', { static: true })
  contactsAutocomplete: GeneralAutocompleteComponent;
  newContact;
  valuesChanged = false;
  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { mode: NoteMode; fromContact: boolean; note: Note },
    private dialogRef: MatDialogRef<AddNoteDialogComponent>,
    private store: Store<AppState>,
    private formBuilder: FormBuilder,
    public stringUtils: StringUtilsService,
    private categoryService: CategoryService,
    private contactsService: ContactsService,
    private filteredViewService: FilteredViewService,
    private relationshipsService: ContactRelationshipsService,
    public dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    this.isCreate = this.data.mode === NoteMode.CREATE;
    this.isFilteredView = this.filteredViewService.isFilterView.value;
    this.form = this.formBuilder.group({
      subject: [this.data.note?.subject, Validators.required],
      description: [this.data.note?.description, Validators.required],
      category: [null, Validators.required],
      entities: [this.data.note?.entities, Validators.required],
      contacts: [this.data.note?.contacts],
      started: [
        {
          value: this.data.note?.started ? new Date(this.data.note.started) : new Date(),
          disabled: true,
        },
        Validators.required,
      ],
      pinned: [!!this.data.note?.pinned, Validators.required],
      providedByContact: [
        {
          value:
            this.data.fromContact || this.isFilteredView
              ? true
              : this.data.note?.provided_by_contact,
          disabled: this.data.fromContact || this.isFilteredView,
        },
        Validators.required,
      ],
    });

    if (this.isCreate) this.form.addControl('createTask', this.formBuilder.control(false));

    if (!this.data.fromContact && !this.isFilteredView)
      this.subscriptions.add(
        this.form.get('entities').valueChanges.subscribe((entities) => {
          this.getContactsAutocomplete$ = this.contactsService.searchContactsFollowups.bind(
            this.contactsService,
            entities?.map((e) => e.id),
          );
          this.resetContactsCtrl();
        }),
      );

    this.subscriptions.add(
      this.form.get('providedByContact').valueChanges.subscribe(() => this.resetContactsCtrl()),
    );
    const contactSub = this.store
      .pipe(select(selectLastCreatedContact))
      .subscribe((res) => (this.newContact = res));
    this.subscriptions.add(contactSub);
    this.subscriptions.add(
      this.store
        .pipe(
          select(currentUser),
          switchMap((user) => this.categoryService.getTeamByIds(user.categories?.map((c) => +c))),
        )
        .subscribe((res) => {
          if (!res?.data?.length) return;

          this.userCategories = res.data;
          if (this.data.note?.category_id) {
            this.form
              .get('category')
              .setValue(res.data.find((c) => c.id === this.data.note.category_id));
            this.showCategoryInput = false;
          }
        }),
    );
    this.form.valueChanges.subscribe(() => {
      if (this.form.dirty) {
        this.valuesChanged = true;
      }
    });
  }

  ngAfterViewInit(): void {
    const entitiesCtrl = this.form.get('entities');
    const contactsCtrl = this.form.get('contacts');

    if (entitiesCtrl.value?.length) {
      this.subscriptions.add(
        this.relationshipsService
          .getEntitiesById(entitiesCtrl.value.map((e) => e.id))
          .subscribe((res) => {
            if (res?.data?.length) {
              const entities = res.data as unknown as NoteEntity[];

              entitiesCtrl.setValue(entities);
              this.entitiesStringSub.next(this.formatEntities(entities));
              this.entitiesAutocomplete?.setValue(entities);
              this.entitiesAutocomplete?.updateValueAndValidity();
            }
          }),
      );
    } else entitiesCtrl.setValue(null);

    setTimeout(() => {
      if (this.data.note?.contacts?.length) {
        contactsCtrl.setValue(this.data.note?.contacts);
        this.contactsAutocomplete?.setValue(this.data.note?.contacts);
        this.contactsAutocomplete?.updateValueAndValidity();
      } else if (this.isCreate && this.isFilteredView) {
        const contacts = [this.filteredViewService.getFilteredObject().contact];

        contactsCtrl.setValue(contacts);
        this.contactsAutocomplete?.setValue(contacts);
        this.contactsAutocomplete?.updateValueAndValidity();
      }
    }, 300);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  resetContactsCtrl(): void {
    this.form.get('contacts').setValue(null);
    this.contactsAutocomplete?.setValue(null);
  }

  formatEntities(entities: NoteEntity[]): string {
    const entitiesMap = {};
    let entitiesStr = '';

    Object.values(NoteEntityType).forEach((type) => (entitiesMap[type] = []));
    entities?.forEach((entity) => entitiesMap[entity.entity_type].push(entity));

    for (const [key, value] of Object.entries(entitiesMap)) {
      if ((value as NoteEntity[]).length)
        entitiesStr += `
        <div class="d-flex mb-1">
          <div style="width: 60px !important" class="font-weight-bold">${this.stringUtils.formatEnumString(
            key,
          )}:</div>
          <div class="flex-grow-1 d-flex flex-column">${(value as NoteEntity[])
            .map(
              (entity) => `
              <span class="text-black-50">
                <small>${
                  entity.name || (entity.first_name || '') + ' ' + (entity.last_name || '')
                }</small>
                ${
                  entity.entity_type === NoteEntityType.PATIENT && !!entity.entity_id
                    ? `<small class="ml-2">(${entity.entity_id})</small>`
                    : ''
                }
              </span>
            `,
            )
            .join('')}</div>
        </div>
      `;
    }

    return entitiesStr;
  }

  toggleDropdownMenu(event: Event, elementRef: HTMLElement): void {
    event?.stopPropagation();

    elementRef.classList.contains('show')
      ? elementRef.classList.remove('show')
      : elementRef.classList.add('show');
  }

  handleEntityTypeChange(entityType: NoteEntityType): void {
    if (!entityType) return;

    this.getEntitiesAutocomplete$ = this.relationshipsService.findEntities.bind(
      this.relationshipsService,
      {
        entityTypes: [entityType],
        relatedContactsIds:
          this.data.fromContact || this.isFilteredView
            ? this.form.get('contacts').value?.map((c) => c.id)
            : null,
      },
    );
  }

  formatContactOption(entity: NoteEntity): string {
    return entity?.id
      ? (entity.name || `${entity.first_name || ''} ${entity.last_name || ''}`).trim()
      : '';
  }
  AddContactHandler() {
    const dialogRef_ = this.dialog.open(CreateContactDialogComponent, {
      width: '95vw',
      maxWidth: '95vw',
    });
    dialogRef_.afterClosed().subscribe((result) => {
      if (result && result.added) {
        this.contactsAutocomplete.setValue([...this.contactsAutocomplete.value, this.newContact]);
        this.contactsAutocomplete?.updateValueAndValidity();
      }
    });
  }
  formatNewEntity(entity: NoteEntity): NoteEntity {
    return entity;
  }

  handleEntitiesChange(event: Event, elementRef: HTMLElement): void {
    event.stopPropagation();

    this.form.get('entities').setValue(this.entitiesAutocomplete.value);
    this.entitiesStringSub.next(this.formatEntities(this.entitiesAutocomplete.value));
    this.entityTypeSelect.value = '';
    this.toggleDropdownMenu(null, elementRef);
  }

  toggleCategory(event?: Event): void {
    event?.stopPropagation();

    this.showCategoryInput = !this.showCategoryInput;
  }

  handleCategoryChange(event: Event): void {
    event.stopPropagation();

    if (this.form.get('category').value?.id) this.toggleCategory();
  }

  handleContactsChange(contacts: NoteEntity[]): void {
    this.form.get('contacts').setValue(contacts);
  }

  hasError(fieldName: string, error: string): boolean {
    const fieldCtrl = this.form.get(fieldName);

    return fieldCtrl.touched && fieldCtrl.hasError(error);
  }

  cancel(event: Event, data?: any): void {
    event?.stopPropagation();

    this.dialogRef.close(data);
  }
  close(event: Event): void {
    event?.stopPropagation();

    this.valuesChanged === true ? this.ConfirmClose() : this.dialogRef.close();
  }
  ConfirmClose() {
    const cofirmActionDialogRef = this.dialog.open(ConfirmActionDialogComponent, {
      data: {
        title: 'Close Task Information ',
        description: 'If you close you will lose the changes you have made.',
        waitDesciption: 'Loading...',
      },
      width: '440px',
    });
    cofirmActionDialogRef.afterClosed().subscribe((res) => {
      if (!res) {
        return;
      }
      this.dialogRef.close();
    });
  }

  save(event: Event): void {
    event.stopPropagation();

    this.form.markAllAsTouched();
    this.contactsAutocomplete.updateValueAndValidity();

    if (this.form.invalid || this.contactsAutocomplete.isInvalid()) return;

    this.submitted = true;

    const formCtrls = this.form.controls;
    const note = new Note(
      this.data.note?.id,
      formCtrls.subject.value,
      formCtrls.description.value,
      formCtrls.category.value?.id,
      formCtrls.started.value,
      formCtrls.pinned.value,
      formCtrls.providedByContact.value,
      formCtrls.entities.value?.map((e) => ({ id: e.id, entity_type: e.entity_type })),
      formCtrls.contacts.value?.map((c) => ({ id: c.id, entity_type: NoteEntityType.CONTACT })),
      !!formCtrls.createTask?.value,
    );

    for (const [key, value] of Object.entries(note)) {
      if (typeof value === 'string') note[key] = value.trim();
    }

    this.data.note?.id
      ? this.store.dispatch(
          new UpdateNoteOnServer({ note, close: this.dialogRef.close.bind(this.dialogRef) }),
        )
      : this.store.dispatch(
          new CreateNoteOnServer({ note, close: this.dialogRef.close.bind(this.dialogRef) }),
        );
  }
}
